import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import { enUS, he } from 'date-fns/locale';
import { Button } from '@/shared/components/ui/Button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/Card';
import { ArrowLeft, CheckCircle2, FileText, Calendar } from 'lucide-react';
import { useGetOneOnOneById } from '@/shared/hooks/useOneOnOne';
import { UserSelectorAvatar } from '@/shared/components/user-selector/UserSelectorAvatar';
import { useEmployee } from '@/shared/hooks/useEmployees';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';

interface CompletedSummaryViewProps {
  sessionId: string;
  employeeId: string;
}

export const CompletedSummaryView = ({
  sessionId,
  employeeId,
}: CompletedSummaryViewProps) => {
  const { t, i18n } = useTranslation('oneOnOne');
  const navigate = useNavigate();
  const currentEmployee = useEmployeeContext();

  // Fetch one-on-one session data
  const { data: oneOnOneData, isLoading: isLoadingOneOnOne } =
    useGetOneOnOneById(sessionId);

  // Fetch employee data for the header
  const { data: employee } = useEmployee(employeeId, currentEmployee.companyId);

  const locales = {
    en: enUS,
    he: he,
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMMM d, yyyy', {
      locale: locales[i18n.language as 'en' | 'he'] || enUS,
    });
  };

  const formatTime = (dateString: string) => {
    return format(new Date(dateString), 'h:mm a', {
      locale: locales[i18n.language as 'en' | 'he'] || enUS,
    });
  };

  if (isLoadingOneOnOne) {
    return (
      <Card className="border-none bg-white/90 shadow-lg backdrop-blur">
        <CardContent className="flex items-center justify-center gap-3 py-8">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-primary" />
          <p className="text-muted-foreground">{t('loading')}</p>
        </CardContent>
      </Card>
    );
  }

  const notes = oneOnOneData?.notes?.manager || [];

  return (
    <div className="space-y-8">
      {/* Back Navigation */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: '/one-on-one' })}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          {t('backToOneOnOnes')}
        </Button>
      </div>

      {/* Header */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-6">
          {/* Title - Left Side */}
          <div className="flex min-w-[240px] items-center gap-2 text-slate-900 justify-start">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            <h2 className="text-2xl font-semibold">{t('completedOneOnOne')}</h2>
          </div>

          {/* Employee Info - Right Side */}
          {employee && (
            <div className="flex items-center gap-4">
              <UserSelectorAvatar
                fullName={`${employee.personalInfo.firstName} ${employee.personalInfo.lastName}`}
                imageUrl={employee.personalInfo.imageUrl}
                size="lg"
              />
              <div>
                <p className="text-lg font-semibold text-slate-900">
                  {employee.personalInfo.firstName}{' '}
                  {employee.personalInfo.lastName}
                </p>
                {oneOnOneData?.schedualedDate && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>
                      {formatDate(oneOnOneData.schedualedDate)} •{' '}
                      {formatTime(oneOnOneData.schedualedDate)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Meeting Notes Section */}
      <Card className="border-none bg-white/90 shadow-lg backdrop-blur">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl text-slate-900">
            <FileText className="h-5 w-5 text-primary" />
            {t('meetingNotes')}
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('meetingNotesFromSession')}
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          {notes.length > 0 ? (
            <div className="space-y-2">
              {notes.map((note, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-3"
                >
                  <p className="whitespace-pre-wrap text-sm text-slate-900">
                    {note}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center">
              <p className="text-muted-foreground">{t('noNotesAdded')}</p>
            </div>
          )}

          {/* Note Count */}
          {notes.length > 0 && (
            <div className="pt-2 border-t border-slate-200">
              <p className="text-xs text-muted-foreground text-center">
                {t('noteCount')(notes.length)}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
